import { useEffect, useState } from 'react';
import axios from 'axios';
import { readPrerenderedPageData } from '../utils/prerenderedPageData';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const STORAGE_KEY = 'announcementLastSeen';

export interface Announcement {
  message: string;
  link: string;
  linkText: string;
  isActive: boolean;
}

const EMPTY: Announcement = { message: '', link: '', linkText: '', isActive: false };

/**
 * The announcement to show before the live one arrives: the one baked into a prerendered
 * page, else the one this browser saw last. Starting from the real banner (instead of
 * none) keeps the navbar from jumping down 40px when the request returns.
 */
function initialAnnouncement(): Announcement {
  const baked = readPrerenderedPageData()?.announcement;
  if (baked) return { ...EMPTY, ...baked };
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...EMPTY, ...JSON.parse(stored) };
  } catch {
    // unreadable storage: start without a banner
  }
  return EMPTY;
}

/** Fetches the admin-editable site-wide announcement banner. isActive: false means
 *  "don't show a banner"; if the request fails, the banner shown so far stays. */
export function useAnnouncement(): Announcement {
  const [announcement, setAnnouncement] = useState<Announcement>(initialAnnouncement);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/journal/announcement`)
      .then((res) => {
        const fresh = { ...EMPTY, ...res.data };
        setAnnouncement(fresh);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
        } catch {
          // storage full or blocked: nothing to remember
        }
      })
      .catch(() => {
        // keep what is showing
      });
  }, []);

  return announcement;
}

export default useAnnouncement;
